import { useMemo } from "react";
import { themeSchema } from "@a2z/storefront-schemas";
import type { FieldSchema, ThemeTokens } from "@a2z/storefront-schemas";
import { FieldRenderer } from "../fields/FieldRenderer";
import { evaluateShowIf, withFieldDefaults } from "../lib/showIf";
import type { ThemeDraftApi } from "../state/useThemeDraft";

/**
 * The theme-level MOTION control in the admin studio — the scroll-reveal driver
 * (`sectionReveal` / `sectionRevealStyle` / `sectionRevealSpeed`). The studio's
 * per-section inspector is schema-driven off section settings; the brand tokens
 * (`settings`) had no visual control other than the raw-JSON escape hatch, so
 * this exposes the reveal control the seller ThemeStep also surfaces — driven
 * off the SAME `themeSchema` fields, so the two editors cannot drift.
 *
 * Scoped to the reveal fields on purpose: this is the one brand token the
 * 13-family port needs on both editors, not a full brand inspector (that
 * extraction is out of scope — see docs/plans/2026-07-20-admin-theme-studio.md).
 */
const REVEAL_FIELD_IDS = [
  "sectionReveal",
  "sectionRevealStyle",
  "sectionRevealSpeed",
] as const;

const REVEAL_FIELDS: FieldSchema[] = themeSchema.layout.filter((f) =>
  (REVEAL_FIELD_IDS as readonly string[]).includes(f.id),
);

export function BrandMotionPanel({
  draft,
  lang,
}: {
  draft: ThemeDraftApi;
  lang: "ar" | "en";
}) {
  const settings = draft.draft.settings as unknown as Record<string, unknown>;
  const effective = useMemo(
    () => withFieldDefaults(REVEAL_FIELDS, settings),
    [settings],
  );

  return (
    <details className="rounded-card border border-border bg-card px-4 py-3">
      <summary className="cursor-pointer select-none text-secondary font-bold text-text">
        {lang === "ar" ? "الحركة (ظهور الأقسام)" : "Motion (section reveal)"}
      </summary>
      <div className="mt-3 flex max-w-md flex-col gap-3">
        {REVEAL_FIELDS.filter((field) =>
          evaluateShowIf(field.showIf, effective),
        ).map((field) => (
          <FieldRenderer
            key={field.id}
            field={field}
            value={settings[field.id]}
            lang={lang}
            onChange={(value) =>
              draft.updateSettings({
                [field.id]: value,
              } as Partial<ThemeTokens>)
            }
          />
        ))}
      </div>
    </details>
  );
}
